export const MAX_SQUAD_MEMBERS = 20;

export const SQUAD_ICON_KEYS = [
  "compass",
  "ski",
  "surf",
  "mtb",
  "sailing",
  "water",
  "festival",
  "sun",
  "snow",
  "wave",
] as const;

export type SquadIconKey = (typeof SQUAD_ICON_KEYS)[number];

export type SquadInviteDecision =
  | { ok: true }
  | { ok: false; reason: "expired" | "revoked" | "full" | "used-up" };

export type SquadInviteCheckInput = {
  activeMemberCount: number;
  inviteExpiresAt: string | null;
  inviteRevokedAt: string | null;
  inviteMaxUses: number | null;
  inviteAcceptedCount: number;
  now: Date;
};

export type SquadOwnershipMember = {
  userId: string;
  role: "owner" | "member";
  status: "invited" | "confirmed" | "left" | "removed";
  joinedAt: string;
};

const MAX_SQUAD_NAME_LENGTH = 80;
const MIN_SQUAD_NAME_LENGTH = 2;
const SQUAD_ICON_KEY_SET: ReadonlySet<string> = new Set(SQUAD_ICON_KEYS);
const NO_SQUAD_FEED_EMPTY_COPY = "Create a squad to see what your mates are up to.";
const QUIET_SQUAD_FEED_EMPTY_COPY = "Nothing from your squad this week. Get out there first.";
const SQUAD_JOIN_DISCLOSURE_COPY =
  "Joining shows this Squad your display name, basic session activity, roll call plans, status pins, Steeze, opted-in leaderboard results and opted-in run PBs. Your GPS tracks do not appear in the normal feed.";

export function normaliseSquadName(name: string): string | null {
  const trimmed = name.trim().replace(/\s+/g, " ");
  if (trimmed.length < MIN_SQUAD_NAME_LENGTH || trimmed.length > MAX_SQUAD_NAME_LENGTH) {
    return null;
  }
  return trimmed;
}

export function normaliseSquadIconKey(iconKey: string | null | undefined): string | null {
  const normalised = iconKey?.trim().toLowerCase() ?? "";
  if (!normalised) return null;
  return isSquadIconKey(normalised) ? normalised : null;
}

export function canAcceptSquadInvite(input: SquadInviteCheckInput): SquadInviteDecision {
  if (input.inviteRevokedAt) {
    return { ok: false, reason: "revoked" };
  }

  if (isExpired(input.inviteExpiresAt, input.now)) {
    return { ok: false, reason: "expired" };
  }

  if (input.activeMemberCount >= MAX_SQUAD_MEMBERS) {
    return { ok: false, reason: "full" };
  }

  if (input.inviteMaxUses !== null && input.inviteAcceptedCount >= input.inviteMaxUses) {
    return { ok: false, reason: "used-up" };
  }

  return { ok: true };
}

export function nextSquadOwner(
  members: SquadOwnershipMember[],
  leavingUserId: string,
): string | null {
  const [nextOwner] = members
    .filter((member) => member.userId !== leavingUserId && member.status === "confirmed")
    .sort(compareJoinedAt);

  return nextOwner?.userId ?? null;
}

export function formatSquadMemberCap(activeMemberCount: number): string {
  return `${clampMemberCount(activeMemberCount)} of ${MAX_SQUAD_MEMBERS} members`;
}

export function getSquadFeedEmptyStateCopy(hasSquads: boolean): string {
  return hasSquads ? QUIET_SQUAD_FEED_EMPTY_COPY : NO_SQUAD_FEED_EMPTY_COPY;
}

export function getSquadJoinDisclosureCopy(): string {
  return SQUAD_JOIN_DISCLOSURE_COPY;
}

function isSquadIconKey(value: string): value is SquadIconKey {
  return SQUAD_ICON_KEY_SET.has(value);
}

function isExpired(expiresAt: string | null, now: Date): boolean {
  if (!expiresAt) return false;

  const expiryTime = Date.parse(expiresAt);
  return Number.isNaN(expiryTime) || expiryTime <= now.getTime();
}

function compareJoinedAt(left: SquadOwnershipMember, right: SquadOwnershipMember): number {
  const leftTime = joinedAtTime(left.joinedAt);
  const rightTime = joinedAtTime(right.joinedAt);
  if (leftTime !== rightTime) return leftTime - rightTime;
  return left.userId.localeCompare(right.userId);
}

function joinedAtTime(joinedAt: string): number {
  const time = Date.parse(joinedAt);
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
}

function clampMemberCount(activeMemberCount: number): number {
  if (!Number.isFinite(activeMemberCount)) return 0;
  return Math.min(MAX_SQUAD_MEMBERS, Math.max(0, Math.floor(activeMemberCount)));
}
